import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

export const USER_LEVELS = [1, 2, 3, 4, 5, 6];

export const EMPTY_FILTERS = {
  levels: { enabled: false, values: [] },
  signupDate: { enabled: false, start: null, end: null },
  lineupDate: { enabled: false, start: null, end: null },
  coinBalance: { enabled: false, min: "", max: "" },
  completedProfile: { enabled: false, value: true },
  payingUser: { enabled: false, value: true },
};

// A section header with its on/off toggle. Everything below it is disabled
// until the toggle is on, which keeps "off" reading as "don't filter on this".
function FilterSection({ title, description, enabled, onToggle, children }) {
  return (
    <>
      <Box sx={{ px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            checked={enabled}
            onChange={(event) => onToggle(event.target.checked)}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">{title}</Typography>
            {description && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
        {enabled && <Box sx={{ mt: 2, pl: 6 }}>{children}</Box>}
      </Box>
      <Divider />
    </>
  );
}

export default function UserRewardFilters({ filters, onChange, disabled }) {
  const patch = (key, value) => {
    onChange({ ...filters, [key]: { ...filters[key], ...value } });
  };

  const toggleLevel = (level, checked) => {
    const values = checked
      ? [...filters.levels.values, level]
      : filters.levels.values.filter((item) => item !== level);
    patch("levels", { values: values.sort((a, b) => a - b) });
  };

  return (
    <Card sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="h6">Audience</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          With every filter off this targets all users. Switch one on to narrow
          it down.
        </Typography>
      </Box>
      <Divider />

      <FilterSection
        title="User Level"
        description="Reward only users on the selected levels"
        enabled={filters.levels.enabled}
        onToggle={(enabled) => patch("levels", { enabled })}
      >
        <Stack direction="row" flexWrap="wrap">
          {USER_LEVELS.map((level) => (
            <FormControlLabel
              key={level}
              control={
                <Checkbox
                  checked={filters.levels.values.includes(level)}
                  onChange={(event) => toggleLevel(level, event.target.checked)}
                />
              }
              label={`Level ${level}`}
            />
          ))}
        </Stack>
        {filters.levels.values.length === 0 && (
          <Typography variant="caption" color="error">
            Select at least one level, or switch this filter off.
          </Typography>
        )}
      </FilterSection>

      <FilterSection
        title="Signup Date Range"
        description="Users who registered inside this window"
        enabled={filters.signupDate.enabled}
        onToggle={(enabled) => patch("signupDate", { enabled })}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Start"
                value={filters.signupDate.start ? moment(filters.signupDate.start) : null}
                onChange={(value) =>
                  patch("signupDate", { start: value ? value.toISOString() : null })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="End"
                value={filters.signupDate.end ? moment(filters.signupDate.end) : null}
                onChange={(value) =>
                  patch("signupDate", { end: value ? value.toISOString() : null })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </FilterSection>

      <FilterSection
        title="Lineup Date Range"
        description="Users who entered a lineup inside this window"
        enabled={filters.lineupDate.enabled}
        onToggle={(enabled) => patch("lineupDate", { enabled })}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Start"
                value={filters.lineupDate.start ? moment(filters.lineupDate.start) : null}
                onChange={(value) =>
                  patch("lineupDate", { start: value ? value.toISOString() : null })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="End"
                value={filters.lineupDate.end ? moment(filters.lineupDate.end) : null}
                onChange={(value) =>
                  patch("lineupDate", { end: value ? value.toISOString() : null })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </FilterSection>

      <FilterSection
        title="Coin Balance"
        description="Current balance between min and max (inclusive)"
        enabled={filters.coinBalance.enabled}
        onToggle={(enabled) => patch("coinBalance", { enabled })}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Min"
              type="number"
              fullWidth
              value={filters.coinBalance.min}
              onChange={(event) => patch("coinBalance", { min: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Max"
              type="number"
              fullWidth
              value={filters.coinBalance.max}
              onChange={(event) => patch("coinBalance", { max: event.target.value })}
            />
          </Grid>
        </Grid>
      </FilterSection>

      <FilterSection
        title="Completed Profile"
        enabled={filters.completedProfile.enabled}
        onToggle={(enabled) => patch("completedProfile", { enabled })}
      >
        <RadioGroup
          row
          value={filters.completedProfile.value ? "yes" : "no"}
          onChange={(event) =>
            patch("completedProfile", { value: event.target.value === "yes" })
          }
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FilterSection>

      <FilterSection
        title="Paying User"
        description="Has at least one store purchase"
        enabled={filters.payingUser.enabled}
        onToggle={(enabled) => patch("payingUser", { enabled })}
      >
        <RadioGroup
          row
          value={filters.payingUser.value ? "yes" : "no"}
          onChange={(event) =>
            patch("payingUser", { value: event.target.value === "yes" })
          }
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FilterSection>
    </Card>
  );
}
